import { CheckSquareTwoTone } from "@ant-design/icons";
import {
  BriefNotificationActionType,
  type IBriefNotification,
} from "@shared/lib/types/brief";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import { Avatar, Button, Modal } from "@shared/ui";
import { Collapse, Flex, List, Space, Typography } from "antd";
import styles from "../styles.module.scss";

type ResultItem = IBriefNotification["result"][number];

interface NotificationResultsModalProps {
  open: boolean;
  title: string;
  result: ResultItem[];
  actionType?: BriefNotificationActionType;
  onOkay: () => void;
}

const { Text } = Typography;

export function NotificationResultsModal({
  open,
  title,
  result,
  actionType,
  onOkay,
}: NotificationResultsModalProps) {
  const failedItems = result.filter((item) => item.status === "failed");
  const successItems = result.filter((item) => item.status === "success");

  return (
    <Modal
      open={open}
      title={title}
      centered
      destroyOnClose
      zIndex={10001}
      footer={
        <Button
          type="primary"
          onClick={onOkay}
          style={{ background: "#33312F", margin: "8px 16px" }}
        >
          Okay
        </Button>
      }
      onCancel={onOkay}
      className={styles.resultsModal}
    >
      <div className={styles.resultsModalBody}>
        <Flex vertical gap={8}>
          {failedItems.length > 0 && (
            <Collapse
              ghost
              defaultActiveKey={["failed"]}
              expandIcon={() => <></>}
              bordered={false}
              items={[
                {
                  key: "failed",
                  styles: {
                    header: {
                      background: "#F8E0DE",
                      borderRadius: 8,
                      borderColor: "#F8E0DE",
                    },
                  },
                  label: (
                    <div>
                      <Text>
                        <Text strong>{failedItems.length}</Text> Creators
                        processing{" "}
                        <Text strong style={{ color: "#E11D48" }}>
                          failed
                        </Text>
                        .
                      </Text>
                    </div>
                  ),
                  collapsible: "disabled",
                  children: (
                    <List
                      dataSource={failedItems}
                      renderItem={(item) => (
                        <ResultListItem
                          item={item}
                          isSuccess={false}
                          actionType={actionType}
                        />
                      )}
                    />
                  ),
                },
              ]}
            />
          )}
          {successItems.length > 0 && (
            <Collapse
              defaultActiveKey={["success"]}
              ghost
              bordered={false}
              expandIcon={() => <></>}
              items={[
                {
                  key: "success",
                  styles: {
                    header: {
                      background: "#E5F2E8",
                      borderRadius: 8,
                      borderColor: "#E5F2E8",
                    },
                  },
                  label: (
                    <div>
                      <Text>
                        <Text strong>{successItems.length}</Text> Creators
                        processing{" "}
                        <Text strong style={{ color: "#00875A" }}>
                          successfully
                        </Text>
                        .
                      </Text>
                    </div>
                  ),
                  children: (
                    <List
                      dataSource={successItems}
                      renderItem={(item) => (
                        <ResultListItem
                          item={item}
                          isSuccess
                          actionType={actionType}
                        />
                      )}
                    />
                  ),
                },
              ]}
            />
          )}
        </Flex>
      </div>
    </Modal>
  );
}

function ResultListItem({
  item,
  isSuccess,
  actionType,
}: {
  item: ResultItem;
  isSuccess: boolean;
  actionType?: BriefNotificationActionType;
}) {
  const socialMedia =
    actionType === BriefNotificationActionType.addSocialMediaToBrief
      ? item.socialMedia
      : actionType &&
          [
            BriefNotificationActionType.attachBriefToCampaignInfluencerTask,
            BriefNotificationActionType.assignOperationsPocToCampaignInfluencer,
            BriefNotificationActionType.editOrAddScriptToCampaignInfluencerTask,
            BriefNotificationActionType.markPendingReviewAsSeenOnCampaignInfluencer,
            BriefNotificationActionType.setCustomColumnsOnCampaignInfluencer,
          ].includes(actionType)
        ? item.campaignInfluencer?.socialMedia
        : (item.briefInfluencer || item.oldBriefInfluencer)?.socialMedia;

  return (
    <List.Item>
      <List.Item.Meta
        title={
          <Flex justify="space-between" align="center">
            <Space align="center">
              <Button
                style={{ padding: 0 }}
                type="link"
                onClick={() => window.open(socialMedia?.link, "_blank")}
              >
                {getPlatformIcon(socialMedia?.platform ?? "")}
                <Avatar
                  shape="circle"
                  size="small"
                  src={socialMedia?.profilePicUrl}
                />
                <Text strong>{socialMedia?.username}</Text>
              </Button>
            </Space>
            {isSuccess ? <CheckSquareTwoTone twoToneColor="#34C759" /> : null}
          </Flex>
        }
        description={
          !isSuccess && item.reason ? (
            <Text type="secondary" className={styles.resultErrorDescription}>
              {item.reason}
            </Text>
          ) : undefined
        }
      />
    </List.Item>
  );
}
